<template>
  <div ref="root" class="plate-keyboard-root">
    <el-input
      ref="field"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      clearable
      class="plate-keyboard-field"
      :class="{ 'plate-keyboard-field--prefix': showSearchPrefix }"
      @input="onFieldInput"
      @clear="onClear"
      @focus="onFieldFocus"
      @blur="onFieldBlur"
    >
      <template v-if="showSearchPrefix" slot="prefix">
        <i class="el-icon-search plate-keyboard-prefix" aria-hidden="true" />
      </template>
      <template slot="suffix">
        <button
          type="button"
          class="plate-keyboard-trigger"
          :disabled="disabled"
          title="车牌键盘"
          aria-label="打开车牌键盘"
          @mousedown.prevent.stop="togglePanel"
        >
          <svg class="plate-keyboard-trigger__svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" ry="2" fill="none" stroke="currentColor" stroke-width="1.5" />
            <path d="M7 9h2M11 9h2M15 9h2M7 12h10M7 15h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
      </template>
    </el-input>

    <section
      v-show="show"
      ref="panel"
      class="plate-keyboard-panel"
      :style="panelStyle"
      @mousedown.prevent.stop
    >
      <div class="plate-keyboard-panel__header">
        <button type="button" class="plate-keyboard-panel__close" title="收起" aria-label="收起键盘" @click="panelHide('close')">
          <i class="el-icon-arrow-down" />
        </button>
      </div>
      <div class="plate-keyboard-panel__grid">
        <button
          v-for="(item, i) in currentKeys"
          :key="'k-' + i + '-' + item"
          type="button"
          class="plate-keyboard-panel__key"
          :class="{ 'plate-keyboard-panel__key--wide': keyIsWide(item) }"
          @click="clickKey(item)"
        >
          {{ item }}
        </button>
      </div>
      <div class="plate-keyboard-panel__actions">
        <button
          v-if="showPanelSearch && plateLen > 0"
          type="button"
          class="plate-keyboard-panel__search"
          @click="onPanelSearch"
        >
          搜索
        </button>
        <button type="button" class="plate-keyboard-panel__backspace" title="退格" aria-label="退格" @click="backspace">
          <i class="el-icon-delete" />
        </button>
      </div>
    </section>
  </div>
</template>

<script>
/** 与 @/utils/searchFilterValidate 中车牌省份简称一致，并含「使」 */
const DEFAULT_AREA = [
  '京', '津', '渝', '沪', '冀', '晋', '辽', '吉', '黑', '苏', '浙', '皖', '闽', '赣', '鲁', '豫', '鄂', '湘',
  '粤', '琼', '川', '贵', '云', '陕', '甘', '青', '蒙', '桂', '宁', '新', '藏', '使'
]

const LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))

const ALNUM = [
  ...[0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  ...LETTERS
]

const PLATE_MAX = 8

export default {
  name: 'PlateKeyboardInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '输入车牌号'
    },
    /** 是否在键盘区显示「搜索」并触发 panel-search（如独立搜索条场景） */
    showPanelSearch: {
      type: Boolean,
      default: false
    },
    /** 是否显示输入框左侧搜索图标（与图1 搜索条一致；表单里一般关闭） */
    showSearchPrefix: {
      type: Boolean,
      default: false
    },
    areaDatas: {
      type: Array,
      default: () => DEFAULT_AREA
    }
  },
  data() {
    return {
      show: false,
      panelStyle: {}
    }
  },
  computed: {
    plateLen() {
      const s = this.value != null ? String(this.value) : ''
      return s.length
    },
    currentKeys() {
      const len = this.plateLen
      if (len === 0) return this.areaDatas
      if (len === 1) return LETTERS
      return ALNUM
    }
  },
  watch: {
    show(v) {
      if (v) {
        this.$nextTick(() => {
          this.portalPanelToBody()
          this.updatePanelPosition()
          // 首帧布局（含 rem / 字体）偶发滞后，再对齐一次避免第一次打开偏移
          requestAnimationFrame(() => {
            this.updatePanelPosition()
          })
          window.addEventListener('resize', this.updatePanelPosition)
          window.addEventListener('scroll', this.updatePanelPosition, true)
        })
      } else {
        window.removeEventListener('resize', this.updatePanelPosition)
        window.removeEventListener('scroll', this.updatePanelPosition, true)
        this.unportalPanel()
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePanelPosition)
    window.removeEventListener('scroll', this.updatePanelPosition, true)
    document.removeEventListener('mousedown', this.onDocMousedown, true)
    this.unportalPanel()
  },
  methods: {
    keyIsWide(item) {
      return typeof item === 'string' && this.plateLen === 0
    },
    emitValue(val) {
      const v = val != null ? String(val) : ''
      this.$emit('input', v)
      this.$emit('change', v)
    },
    onFieldInput(v) {
      this.emitValue(v)
    },
    onClear() {
      this.emitValue('')
    },
    onFieldFocus() {
      this.$emit('focus')
    },
    onFieldBlur() {
      this.$emit('blur')
    },
    togglePanel() {
      if (this.disabled) return
      if (this.show) {
        this.panelHide('toggle')
        return
      }
      // 首帧在 panelStyle 写入前若以普通流渲染，大块键盘会挤压表单；先脱离文档流并隐藏
      this.panelStyle = {
        position: 'fixed',
        left: '-9999px',
        top: '0',
        width: '360px',
        zIndex: 5100,
        visibility: 'hidden'
      }
      this.show = true
      this.$emit('panel-toggle', true)
      document.addEventListener('mousedown', this.onDocMousedown, true)
    },
    onDocMousedown(e) {
      if (!this.show) return
      const root = this.$refs.root
      const panel = this.$refs.panel
      if (root && root.contains(e.target)) return
      if (panel && panel.contains(e.target)) return
      if (e.target && e.target.closest && e.target.closest('.plate-license-field__dropdown')) return
      this.panelHide('outside')
    },
    panelHide(reason = 'unknown') {
      this.show = false
      this.$emit('panel-toggle', { visible: false, reason })
      document.removeEventListener('mousedown', this.onDocMousedown, true)
    },
    /** fixed 遇 transform 祖先会相对该祖先定位，与 getBoundingClientRect 视口坐标不一致；挂到 body 与坐标一致 */
    portalPanelToBody() {
      const panel = this.$refs.panel
      const root = this.$refs.root
      if (!panel || !root || typeof document === 'undefined') return
      if (panel.parentNode !== document.body) {
        document.body.appendChild(panel)
      }
    },
    unportalPanel() {
      const panel = this.$refs.panel
      const root = this.$refs.root
      if (!panel || !root) return
      if (panel.parentNode === document.body) {
        root.appendChild(panel)
      }
    },
    updatePanelPosition() {
      const root = this.$refs.root
      if (!root) return
      const r = root.getBoundingClientRect()
      const margin = 8
      const vw = typeof window !== 'undefined' ? window.innerWidth : 800
      const desiredW = Math.max(r.width, 360)
      const w = Math.min(desiredW, vw - margin * 2)
      let left = r.left
      if (left + w > vw - margin) left = Math.max(margin, vw - margin - w)
      if (left < margin) left = margin
      this.panelStyle = {
        position: 'fixed',
        top: `${Math.round(r.bottom + 4)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(w)}px`,
        zIndex: 5100,
        visibility: 'visible'
      }
    },
    clickKey(item) {
      const s = this.value != null ? String(this.value) : ''
      if (s.length >= PLATE_MAX) return
      const ch = typeof item === 'number' ? String(item) : String(item)
      let next = s
      if (s.length === 0) {
        if (!this.areaDatas.includes(ch)) return
        next = ch
      } else if (s.length === 1) {
        if (!/[A-Z]/.test(ch)) return
        next = s + ch
      } else {
        if (!/[0-9A-Z]/.test(ch)) return
        next = s + ch
      }
      this.emitValue(next)
    },
    backspace() {
      const s = this.value != null ? String(this.value) : ''
      if (!s.length) return
      this.emitValue(s.slice(0, -1))
    },
    onPanelSearch() {
      this.$emit('panel-search', this.value != null ? String(this.value) : '')
      this.panelHide('search')
    }
  }
}
</script>

<style scoped>
.plate-keyboard-root {
  width: 100%;
  position: relative;
}

.plate-keyboard-field--prefix ::v-deep .el-input__inner {
  padding-left: 2.25rem;
}

.plate-keyboard-field ::v-deep .el-input__suffix {
  display: flex;
  align-items: center;
  right: 0.375rem;
}

.plate-keyboard-field ::v-deep .el-input__suffix-inner {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
}

.plate-keyboard-field ::v-deep .el-input__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  line-height: 1 !important;
  font-size: 0.875rem;
}

.plate-keyboard-prefix {
  color: #909399;
  font-size: 0.95rem;
  line-height: 1;
}

.plate-keyboard-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #606266;
  cursor: pointer;
  flex-shrink: 0;
}

.plate-keyboard-trigger:hover:not(:disabled) {
  background: #f0f2f5;
  color: var(--layout-primary, #087dff);
}

.plate-keyboard-trigger:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.plate-keyboard-trigger__svg {
  display: block;
}

.plate-keyboard-panel {
  box-sizing: border-box;
  background: #f9f7f8;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 0 10px 12px;
  border: 1px solid #e8e8e8;
}

.plate-keyboard-panel__header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 36px;
}

.plate-keyboard-panel__close {
  border: none;
  background: transparent;
  padding: 6px 8px;
  cursor: pointer;
  color: #606266;
  font-size: 1rem;
  line-height: 1;
}

.plate-keyboard-panel__close:hover {
  color: var(--layout-primary, #087dff);
}

.plate-keyboard-panel__grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}

.plate-keyboard-panel__key {
  min-height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;
  font-size: 1rem;
  color: #303133;
  cursor: pointer;
  padding: 6px 4px;
  line-height: 1.2;
}

.plate-keyboard-panel__key:hover {
  border-color: var(--layout-primary, #087dff);
  color: var(--layout-primary, #087dff);
}

.plate-keyboard-panel__key--wide {
  /* 省份键在 6 列网格中与参考图一致 */
  font-size: 1.05rem;
}

.plate-keyboard-panel__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: stretch;
}

.plate-keyboard-panel__search {
  flex: 1;
  max-width: 50%;
  min-height: 44px;
  border: 1px solid var(--layout-primary, #087dff);
  border-radius: 8px;
  background: var(--layout-primary, #087dff);
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
}

.plate-keyboard-panel__search:hover {
  filter: brightness(1.05);
}

.plate-keyboard-panel__backspace {
  flex: 0 0 44%;
  min-height: 44px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;
  color: #606266;
  font-size: 1.25rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.plate-keyboard-panel__backspace:hover {
  border-color: var(--layout-primary, #087dff);
  color: var(--layout-primary, #087dff);
}
</style>
